import './services/navigation-events.js';

const scheduleIdle = callback => {
  if (typeof window === 'undefined') {
    return; // no-op during SSR
  }

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback, { timeout: 2000 });
  } else {
    window.setTimeout(callback, 200);
  }
};

const isProdBuild = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.PROD;

if (typeof window !== 'undefined' && isProdBuild) {
  window.litDisableDevMode = true;
}

scheduleIdle(() => {
  import('./services/analytics-dashboard-store.js');

  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    import('@sentry/browser').then(Sentry => {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: 'production',
        tracesSampleRate: 0.1,
        integrations: [new Sentry.BrowserTracing()]
      });
    });
  }

  if ('serviceWorker' in navigator) {
    import('virtual:pwa-register').then(({ registerSW }) => {
      registerSW({
        onNeedRefresh() {
          if (import.meta.env.DEV) {
            console.log('New content available, please refresh');
          }
        },
        onOfflineReady() {
          if (import.meta.env.DEV) {
            console.log('App ready to work offline');
          }
        }
      });
    });
  }
});

// Add error handlers
window.addEventListener('unhandledrejection', event => {
  console.error('Unhandled promise rejection:', event.reason);
});

window.addEventListener('error', event => {
  console.error('Global error:', event.error);
});

// Define required components
const requiredComponents = [
  'app-header',
  'app-footer',
  'final-cta-band',
  'sticky-cta',
  'cta-flow-manager',
  'support-bubble'
];

// Dynamic imports
async function initializeApp() {
  try {
    const lit = await import('lit');
    const { LitElement, html, css } = lit;

    const { createRouter } = await import('./router.js');

    // Import styles
    await Promise.all([
      import('./styles/reset.css'),
      import('./styles/design-system.css'),
      import('./styles/global.css'),
    ]);

    // Components
    const layoutComponents = [
      () => import('./components/app-header.js'),
      () => import('./components/app-footer.js'),
      () => import('./components/final-cta-band.js'),
      () => import('./components/sticky-cta.js'),
      () => import('./components/cta-flow-manager.js'),
      () => import('./components/support-bubble.js')
    ];

    for (const loader of layoutComponents) {
      await loader();
    }

    // Define the app component
    class CalmConnectApp extends LitElement {
      static properties = {
        currentPath: { type: String },
        isLoading: { type: Boolean },
        hasError: { type: Boolean },
        errorMessage: { type: String },
        componentsLoaded: { type: Boolean },
      };

      static styles = css`
        :host {
          display: block;
          min-height: 100vh;
          background-color: var(--color-secondary-50);
        }

        main {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: calc(100vh - var(--header-height) - var(--footer-height));
          position: relative;
        }

        .page-content {
          animation: fadeIn 0.5s ease-out;
        }

        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(4px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: var(--z-50);
          animation: fadeIn 0.3s ease-out;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid var(--color-primary-500);
          border-top: 4px solid transparent;
          border-radius: var(--radius-full);
          animation: spin 1s linear infinite;
        }

        .error-container {
          text-align: center;
          padding: var(--spacing-16) var(--spacing-4);
          max-width: var(--container-md);
          margin: 0 auto;
          animation: fadeIn 0.5s ease-out;
        }

        .error-container h1 {
          color: var(--color-error-500);
          font-size: var(--font-size-3xl);
          margin-bottom: var(--spacing-4);
          font-family: var(--font-family-display);
        }

        .error-container p {
          color: var(--color-secondary-600);
          margin-bottom: var(--spacing-8);
          font-size: var(--font-size-lg);
        }

        .retry-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: var(--button-height);
          padding: 0 var(--spacing-6);
          background: var(--color-primary-600);
          color: white;
          border-radius: var(--radius-lg);
          font-weight: var(--font-weight-medium);
          transition: var(--transition-all);
          border: none;
          cursor: pointer;
        }

        .retry-button:hover {
          background: var(--color-primary-700);
          transform: translateY(-1px);
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .error-container h1 {
            font-size: var(--font-size-2xl);
          }

          .error-container p {
            font-size: var(--font-size-base);
          }
        }
      `;

      constructor() {
        super();
        this.isLoading = true;
        this.hasError = false;
        this.errorMessage = '';
        this.componentsLoaded = false;

        try {
          // Create router
          this.router = createRouter(this);

          // Handle navigation errors
          window.addEventListener('unhandledrejection', event => {
            console.error('Navigation error:', event.reason);
            this.handleError(event.reason);
          });

          // Check component registration
          this.checkComponentsLoaded();
        } catch (error) {
          console.error('Error in constructor:', error);
          this.handleError(error);
        }
      }

      async checkComponentsLoaded() {
        try {
          // Wait for all components to be defined
          await Promise.all(
            requiredComponents.map(async component => {
              try {
                await customElements.whenDefined(component);
              } catch (error) {
                console.error(`Error defining ${component}:`, error);
                throw error;
              }
            })
          );

          this.componentsLoaded = true;
          this.isLoading = false;
          this.requestUpdate();
        } catch (error) {
          console.error('Error loading components:', error);
          this.handleError(
            new Error('Failed to load required components. Please check the console for details.')
          );
        }
      }

      firstUpdated() {
        // Component first rendered
      }

      updated(changedProperties) {
        // Component updated
      }

      handleError(error) {
        console.error('App error:', error);
        this.hasError = true;
        this.errorMessage = error.message || 'An unexpected error occurred';
        this.isLoading = false;
        this.requestUpdate();
        // Update error text with i18n after rendering
        this.updateComplete.then(() => this.updateErrorText());
      }

      retryNavigation() {
        this.hasError = false;
        this.errorMessage = '';
        this.isLoading = true;
        this.componentsLoaded = false;
        this.checkComponentsLoaded();
        window.location.reload();
      }

      renderLoading() {
        return html`
          <div class="loading-overlay">
            <div class="loading-spinner"></div>
          </div>
        `;
      }

      async loadI18n() {
        // Dynamically import i18n to avoid blocking initial render
        const { i18n } = await import('./i18n/i18n-manager.js');
        return i18n;
      }

      renderError() {
        return html`
          <div class="error-container">
            <h1 id="error-title">Oops! Something went wrong</h1>
            <p id="error-message">${this.errorMessage}</p>
            <button class="retry-button" @click=${this.retryNavigation} id="retry-button">Try Again</button>
          </div>
        `;
      }

      async updateErrorText() {
        try {
          const i18n = await this.loadI18n();
          const titleEl = this.shadowRoot?.querySelector('#error-title');
          const messageEl = this.shadowRoot?.querySelector('#error-message');
          const buttonEl = this.shadowRoot?.querySelector('#retry-button');

          if (titleEl) titleEl.textContent = i18n.t('error.general.title');
          if (buttonEl) buttonEl.textContent = i18n.t('error.general.retry');
        } catch (e) {
          // Fallback to English if i18n fails
          console.error('Error loading i18n for error page:', e);
        }
      }

      render() {
        // Check if we're on the console route
        const isConsole = window.location.pathname.startsWith('/console');

        return html`
          ${this.componentsLoaded
            ? html`
                ${!isConsole ? html`<app-header></app-header>` : ''}
                <main id="main-content">${this.hasError ? this.renderError() : this.router.outlet()}</main>
                ${!isConsole ? html`<final-cta-band></final-cta-band>` : ''}
                ${!isConsole ? html`<app-footer></app-footer>` : ''}
                ${!isConsole ? html`<sticky-cta></sticky-cta><cta-flow-manager></cta-flow-manager>` : ''}
                ${!isConsole ? html`<support-bubble></support-bubble>` : ''}
              `
            : this.renderLoading()}
        `;
  }
}

    // Register the app component
    try {
      customElements.define('calm-connect-app', CalmConnectApp);
    } catch (error) {
      console.error('Failed to register CalmConnectApp:', error);
    }
  } catch (error) {
    console.error('Error during initialization:', error);
  }
}

// Start the application
initializeApp().catch(error => {
  console.error('Failed to initialize app:', error);
});
